import { Routes } from '@angular/router';
import { autorizacaoGuard } from '@cei-ufg/ngx-cei-seguranca';
import { autenticacaoGuard } from '@cei-ufg/ngx-cei-sso-client';
import { ACESSO_PAINEL_ADMINISTRATIVO } from './constants/admin-permissions';
import { LayoutComponent } from './layout/layout.component';
import { AdminDashboardComponent } from './pessoa/pessoa-dashboard/dashboard.component';
import { AdminListComponent } from './pessoa/pessoa-list/list.component';
import { AdminDetailComponent } from './pessoa/pessoa-detail/detail.component';
import { AdminPessoaEditComponent } from './pessoa/pessoa-edit/edit.component';
import { AdminFormComponent } from './pessoa/pessoa-form/form.component';
import { AdminUsuarioDetailComponent } from './usuario/usuario-detail/detail.component';
import { AdminUsuarioEditComponent } from './usuario/usuario-edit/edit.component';
import { AdminGrupoListComponent } from './grupo/grupo-list/list.component';
import { AdminGrupoDetailComponent } from './grupo/grupo-detail/detail.component';
import { AdminGrupoEditComponent } from './grupo/grupo-edit/edit.component';
import { AdminGroupFormComponent } from './grupo/grupo-form/group-form.component';
import { AdminPermissaoListComponent } from './permissao/permissao-list/list.component';
import { AdminPermissaoDetailComponent } from './permissao/permissao-detail/detail.component';
import { AdminPermissaoEditComponent } from './permissao/permissao-edit/edit.component';
import { AdminPermissionFormComponent } from './permissao/permissao-form/permission-form.component';

const adminAccessData = {
  ceiAuthorize: true,
  ceiPermissoes: [ACESSO_PAINEL_ADMINISTRATIVO],
};

/** Rotas administrativas que exigem permissão do painel. */
const rotaProtegidaAdmin = {
  canActivate: [autorizacaoGuard],
  data: adminAccessData,
};

/** Rotas de erro e wildcard não exigem autorização RBAC. */
const rotaSemAutorizacao = {
  data: { ceiAuthorize: false },
};

export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () => import('@cei-ufg/ngx-cei-sso-client').then((m) => m.RedirectComponent),
  },
  {
    path: 'admin',
    component: LayoutComponent,
    canActivate: [autenticacaoGuard],
    children: [
      { path: '', ...rotaProtegidaAdmin, component: AdminDashboardComponent },
      { path: 'pessoas', ...rotaProtegidaAdmin, component: AdminListComponent },
      { path: 'pessoas/incluir', ...rotaProtegidaAdmin, component: AdminFormComponent },
      { path: 'pessoas/detalhe/:id', ...rotaProtegidaAdmin, component: AdminDetailComponent },
      { path: 'pessoas/atualizar', ...rotaProtegidaAdmin, component: AdminPessoaEditComponent },
      { path: 'pessoas/atualizar/:id', ...rotaProtegidaAdmin, component: AdminPessoaEditComponent },
      { path: 'usuarios/detalhe/:id', ...rotaProtegidaAdmin, component: AdminUsuarioDetailComponent },
      { path: 'usuarios/atualizar/:id', ...rotaProtegidaAdmin, component: AdminUsuarioEditComponent },
      { path: 'grupos', ...rotaProtegidaAdmin, component: AdminGrupoListComponent },
      { path: 'grupos/incluir', ...rotaProtegidaAdmin, component: AdminGroupFormComponent },
      { path: 'grupos/detalhe/:id', ...rotaProtegidaAdmin, component: AdminGrupoDetailComponent },
      { path: 'grupos/atualizar/:id', ...rotaProtegidaAdmin, component: AdminGrupoEditComponent },
      { path: 'permissoes', ...rotaProtegidaAdmin, component: AdminPermissaoListComponent },
      { path: 'permissoes/incluir', ...rotaProtegidaAdmin, component: AdminPermissionFormComponent },
      { path: 'permissoes/detalhe/:id', ...rotaProtegidaAdmin, component: AdminPermissaoDetailComponent },
      { path: 'permissoes/atualizar/:id', ...rotaProtegidaAdmin, component: AdminPermissaoEditComponent },
      {
        path: 'aplicacoes',
        ...rotaProtegidaAdmin,
        loadComponent: () =>
          import('./aplicacao/aplicacao-list/list.component').then(
            (m) => m.AdminAplicacaoListComponent,
          ),
      },
      {
        path: 'aplicacoes/incluir',
        ...rotaProtegidaAdmin,
        loadComponent: () =>
          import('./aplicacao/aplicacao-form/form.component').then(
            (m) => m.AdminAplicacaoFormComponent,
          ),
      },
      {
        path: 'aplicacoes/atualizar/:id',
        ...rotaProtegidaAdmin,
        loadComponent: () =>
          import('./aplicacao/aplicacao-edit/edit.component').then(
            (m) => m.AdminAplicacaoEditComponent,
          ),
      },
      {
        path: 'entidades',
        ...rotaProtegidaAdmin,
        loadComponent: () =>
          import('./entidade/entidade-list/list.component').then(
            (m) => m.AdminEntidadeListComponent,
          ),
      },
      {
        path: 'entidades/incluir',
        ...rotaProtegidaAdmin,
        loadComponent: () =>
          import('./entidade/entidade-form/form.component').then(
            (m) => m.AdminEntidadeFormComponent,
          ),
      },
      {
        path: 'entidades/atualizar/:id',
        ...rotaProtegidaAdmin,
        loadComponent: () =>
          import('./entidade/entidade-edit/edit.component').then(
            (m) => m.AdminEntidadeEditComponent,
          ),
      },
      {
        path: 'recursos',
        ...rotaProtegidaAdmin,
        loadComponent: () =>
          import('./recurso/recurso-list/list.component').then((m) => m.AdminRecursoListComponent),
      },
      {
        path: 'recursos/incluir',
        ...rotaProtegidaAdmin,
        loadComponent: () =>
          import('./recurso/recurso-form/form.component').then((m) => m.AdminRecursoFormComponent),
      },
      {
        path: 'recursos/detalhe/:id',
        ...rotaProtegidaAdmin,
        loadComponent: () =>
          import('./recurso/recurso-detail/detail.component').then((m) => m.AdminRecursoDetailComponent),
      },
      {
        path: 'recursos/atualizar/:id',
        ...rotaProtegidaAdmin,
        loadComponent: () =>
          import('./recurso/recurso-edit/edit.component').then((m) => m.AdminRecursoEditComponent),
      },
      {
        path: 'recursos/permissoes',
        ...rotaProtegidaAdmin,
        loadComponent: () =>
          import('./recurso/recurso-permissoes/recurso-permissoes.component').then(
            (m) => m.AdminRecursoPermissoesComponent,
          ),
      },
      {
        path: 'recursos/grupos',
        ...rotaProtegidaAdmin,
        loadComponent: () =>
          import('./recurso/recurso-grupos/recurso-grupos.component').then(
            (m) => m.AdminRecursoGruposComponent,
          ),
      },
      {
        path: 'grupos/recursos',
        ...rotaProtegidaAdmin,
        loadComponent: () =>
          import('./recurso/grupo-recursos/grupo-recursos.component').then(
            (m) => m.AdminGrupoRecursosComponent,
          ),
      },
      {
        path: 'nao-encontrado',
        ...rotaSemAutorizacao,
        loadComponent: () =>
          import('@cei-ufg/ngx-arquitetura-cei-frontend').then((m) => m.NotFound),
        pathMatch: 'full',
      },
      {
        path: 'erro-servidor',
        ...rotaSemAutorizacao,
        loadComponent: () =>
          import('@cei-ufg/ngx-arquitetura-cei-frontend').then((m) => m.ServerError),
        pathMatch: 'full',
      },
      {
        path: 'proibido',
        ...rotaSemAutorizacao,
        loadComponent: () =>
          import('@cei-ufg/ngx-arquitetura-cei-frontend').then((m) => m.ForbiddenError),
        pathMatch: 'full',
      },
      {
        path: '**',
        ...rotaSemAutorizacao,
        redirectTo: 'nao-encontrado',
      },
    ],
  },
  { path: '', redirectTo: '/admin', pathMatch: 'full' },
  { path: 'proibido', redirectTo: '/admin/proibido', pathMatch: 'full' },
  { path: 'nao-encontrado', redirectTo: '/admin/nao-encontrado', pathMatch: 'full' },
  { path: 'erro-servidor', redirectTo: '/admin/erro-servidor', pathMatch: 'full' },
  { path: '**', redirectTo: '/nao-encontrado' },
];
